import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { GigForm, type GigFormData } from '../components/gigs/GigForm';
import { DeleteGigModal } from '../components/gigs/DeleteGigModal';
import { useGetGig } from '../api/queries';
import { useGetTags } from '../api/services';
import { useUpdateGig, useDeleteGig } from '../api/mutations';
import { FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { useMemo, useState } from 'react';
import { toast } from '../utils/toast';
import { Button } from '../components/ui/Button';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '../components/common/UnsavedChangesModal';
import { normalizeGigTagLabels } from '../utils/gigTags';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { UnauthorizedPage } from '../components/common/UnauthorizedPage';

export const Route = createFileRoute('/gigs/$gigId/edit')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: EditGig,
});

function buildDatetime(date: string, time: string): string {
  if (!date) return '';
  const now = new Date();
  now.setMinutes(now.getMinutes() + 1);
  const defaultTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const t = time || defaultTime;
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = t.split(':').map(Number);
  // Gig times are sent "as picked": no timezone conversion, no zone marker.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
}

function parseDatetime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

function getCurrentUser() {
  const userData = localStorage.getItem('user_data');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
}

function EditGig() {
  const { gigId } = Route.useParams();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const { data: gig, isLoading, isFetching, isError } = useGetGig(gigId);
  const { data: allTags = [] } = useGetTags('');
  const { mutate: updateGig, isPending: isUpdating } = useUpdateGig();
  const { mutate: deleteGig, isPending: isDeleting } = useDeleteGig();
  const [isUploading, setIsUploading] = useState(false);
  const { isConfirmOpen, requestExit, confirmExit, cancelExit, allowNavigation } =
    useUnsavedChangesGuard({ isDirty: isFormDirty });
  const tagsById = useMemo(() => new Map(allTags.map((tag) => [tag.id, tag])), [allTags]);
  const tagsByName = useMemo(
    () => new Map(allTags.map((tag) => [tag.name.trim().toLowerCase(), tag])),
    [allTags],
  );

  const userData = getCurrentUser();
  const currentUserId = userData?.id ?? localStorage.getItem('user_id') ?? '';
  const currentUserEmail = userData?.email ?? '';

  const gigAny = gig as Record<string, unknown> | undefined;
  const owner = (gigAny?.owner ?? {}) as Record<string, unknown>;
  const ownerId = String(owner.id ?? gigAny?.user ?? '');
  const ownerEmail = String(owner.email ?? '');

  const isOwner =
    !!currentUserId &&
    !!ownerId &&
    (ownerId === String(currentUserId) ||
      (!!currentUserEmail && currentUserEmail.toLowerCase() === ownerEmail.toLowerCase()));

  if (isLoading || (isFetching && !gig)) {
    return <LoadingLogo />;
  }

  if (isError || !gig) {
    return <UnauthorizedPage resourceType="gig" resourceId={gigId} />;
  }

  if (!isOwner) {
    return <UnauthorizedPage resourceType="gig" resourceId={gigId} />;
  }

  const handleSubmit = async (formData: GigFormData) => {
    if (!gig) return;
    // Build the full desired API payload from the form
    const startDatetime = formData.startDate
      ? buildDatetime(formData.startDate, formData.startTime)
      : null;
    const endDatetime = formData.endDate ? buildDatetime(formData.endDate, formData.endTime) : null;
    const appStartDatetime = formData.applicationStartDate
      ? buildDatetime(formData.applicationStartDate, formData.applicationStartTime)
      : null;
    const appEndDatetime = formData.applicationEndDate
      ? buildDatetime(formData.applicationEndDate, formData.applicationEndTime)
      : null;

    const newLocationBody = formData.locationBody
      ? {
          address: formData.locationBody.address,
          city: formData.locationBody.city,
          state: formData.locationBody.state,
          country: formData.locationBody.country,
          latitude: formData.locationBody.latitude,
          longitude: formData.locationBody.longitude,
        }
      : null;

    // Use JSON-stringified location for stable deep comparison
    const hasBudget =
      formData.budgetType === 'flat_rate'
        ? !!formData.budgetAmount?.trim()
        : !!(formData.budgetMin?.trim() || formData.budgetMax?.trim());

    const desired: Record<string, unknown> = {
      title: formData.title,
      description: formData.description,
      additional_information: formData.additionalInfo,
      timezone: formData.timezone,
      location_body: JSON.stringify(newLocationBody),
      budget_type: hasBudget ? formData.budgetType : null,
      budget_amount:
        hasBudget && formData.budgetType === 'flat_rate'
          ? formData.budgetAmount?.trim() || null
          : null,
      budget_min:
        hasBudget && formData.budgetType === 'range' ? formData.budgetMin?.trim() || null : null,
      budget_max:
        hasBudget && formData.budgetType === 'range' ? formData.budgetMax?.trim() || null : null,
      start_datetime: startDatetime,
      end_datetime: endDatetime,
      application_start_datetime: appStartDatetime,
      application_end_datetime: appEndDatetime,
      tags: JSON.stringify(
        formData.tagIds
          .map((id) => tagsById.get(id)?.name ?? '')
          .filter(Boolean)
          .sort(),
      ),
    };

    // Build original payload from API data for diff
    const origStart = parseDatetime(gig.start_datetime);
    const origEnd = parseDatetime(gig.end_datetime);
    const origStartDatetime = origStart.date ? buildDatetime(origStart.date, origStart.time) : null;
    const origEndDatetime = origEnd.date ? buildDatetime(origEnd.date, origEnd.time) : null;

    // Normalise application datetimes from API through the same buildDatetime path so
    // the comparison is apples-to-apples (both local-time ISO strings).
    const origAppStart = parseDatetime(
      ((gig as Record<string, unknown>).application_start_datetime as string) ?? '',
    );
    const origAppEnd = parseDatetime(
      ((gig as Record<string, unknown>).application_end_datetime as string) ?? '',
    );
    const origAppStartDatetime = origAppStart.date
      ? buildDatetime(origAppStart.date, origAppStart.time)
      : null;
    const origAppEndDatetime = origAppEnd.date
      ? buildDatetime(origAppEnd.date, origAppEnd.time)
      : null;

    // Normalise original location to the same shape we send so diff is stable
    const origLocationNormalized = gig.location
      ? {
          address:
            gig.location.street ??
            [gig.location.city, gig.location.state, gig.location.country]
              .filter(Boolean)
              .join(', '),
          city: gig.location.city ?? '',
          state: gig.location.state ?? '',
          country: gig.location.country ?? '',
          latitude: gig.location.latitude ?? 0,
          longitude: gig.location.longitude ?? 0,
        }
      : null;

    const original: Record<string, unknown> = {
      title: gig.title ?? '',
      description: gig.description ?? '',
      additional_information: gig.additional_information ?? '',
      timezone: gig.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
      location_body: JSON.stringify(origLocationNormalized),
      budget_type: gig.budget_type ?? null,
      budget_amount: gig.budget_amount ?? null,
      budget_min: gig.budget_min ?? null,
      budget_max: gig.budget_max ?? null,
      start_datetime: origStartDatetime,
      end_datetime: origEndDatetime,
      application_start_datetime: origAppStartDatetime,
      application_end_datetime: origAppEndDatetime,
      tags: JSON.stringify(normalizeGigTagLabels((gig as Record<string, unknown>).tags).sort()),
    };

    // Only include fields that have changed — send each field independently
    const patch: Record<string, unknown> = {};

    for (const key of Object.keys(desired)) {
      if (desired[key] !== original[key]) {
        if (key === 'location_body') patch[key] = newLocationBody;
        else if (key === 'tags') patch[key] = formData.tagIds;
        else patch[key] = desired[key];
      }
    }

    // Ensure budget_type is always included in patch if any budget field is being modified
    const hasBudgetChange =
      'budget_type' in patch ||
      'budget_amount' in patch ||
      'budget_min' in patch ||
      'budget_max' in patch;

    if (hasBudgetChange && !('budget_type' in patch)) {
      patch.budget_type = desired.budget_type;
    }

    // If any datetime changes, always include timezone for backend consistency
    const hasAnyDatetimeChanged =
      patch.start_datetime !== undefined ||
      patch.end_datetime !== undefined ||
      patch.application_start_datetime !== undefined ||
      patch.application_end_datetime !== undefined;
    if (hasAnyDatetimeChanged && patch.timezone === undefined) {
      patch.timezone = formData.timezone;
    }

    // Handle image: upload new file if provided, or clear if existing was removed
    const originalImage = gig.image ?? null;
    if (formData.image) {
      // New file selected — upload to Cloudinary
      setIsUploading(true);
      let uploadedUrl: string;
      try {
        const results = await uploadFilesToCloudinary([formData.image], {
          folder: 'gig_images',
          uploadPreset: 'service_preset',
        });
        uploadedUrl = results[0].secure_url;
      } catch {
        toast.error('Image upload failed. Please try again.');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
      patch.image = uploadedUrl;
    } else if (formData.existingImageUrl === null && originalImage !== null) {
      // Existing image was removed by the user
      patch.image = null;
    }
    // else: existingImageUrl unchanged — don't touch the image field

    if (Object.keys(patch).length === 0) {
      toast.info('No changes detected.');
      return;
    }

    updateGig(
      { gigId, data: patch },
      {
        onSuccess: () => {
          allowNavigation();
          navigate({ to: '/gigs/$gigId', params: { gigId }, search: { apply: false } });
        },
        onError: () => {
          // Handled by mutation
        },
      },
    );
  };

  const handleDelete = () => {
    deleteGig(gigId, {
      onSuccess: () => {
        allowNavigation();
        setIsDeleteModalOpen(false);
        navigate({ to: '/gigs' });
      },
      onError: () => {
        setIsDeleteModalOpen(false);
      },
    });
  };

  if (isLoading || (isFetching && !gig)) {
    return <LoadingLogo />;
  }

  if (isError || !gig) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Gig not found</h2>
          <p className="text-zinc-500 mb-6">Unable to load gig for editing.</p>
          <Button onClick={() => navigate({ to: '/gigs' })} size="lg" className="rounded-full px-8">
            Back to Gigs
          </Button>
        </main>
        <BottomNav />
      </div>
    );
  }

  // Pre-fill form from API data
  const startDt = parseDatetime(gig.start_datetime);
  const endDt = parseDatetime(gig.end_datetime);
  const appStartDt = parseDatetime(
    ((gig as Record<string, unknown>).application_start_datetime as string) ?? '',
  );
  const appEndDt = parseDatetime(
    ((gig as Record<string, unknown>).application_end_datetime as string) ?? '',
  );

  const initialData: Partial<GigFormData> = {
    title: gig.title ?? '',
    description: gig.description ?? '',
    additionalInfo: gig.additional_information ?? '',
    locationBody: gig.location
      ? {
          address:
            gig.location.street ??
            [gig.location.city, gig.location.state, gig.location.country]
              .filter(Boolean)
              .join(', '),
          city: gig.location.city ?? '',
          state: gig.location.state ?? '',
          country: gig.location.country ?? '',
          latitude: gig.location.latitude ?? 0,
          longitude: gig.location.longitude ?? 0,
        }
      : null,
    budgetType: (gig.budget_type as GigFormData['budgetType']) ?? 'flat_rate',
    // For flat_rate the API stores the amount in budget_min/budget_max when budget_amount is absent
    budgetAmount: gig.budget_amount ?? gig.budget_min ?? '',
    budgetMin: gig.budget_min ?? '',
    budgetMax: gig.budget_max ?? '',
    startDate: startDt.date,
    startTime: startDt.time,
    endDate: endDt.date,
    endTime: endDt.time,
    applicationStartDate: appStartDt.date,
    applicationStartTime: appStartDt.time,
    applicationEndDate: appEndDt.date,
    applicationEndTime: appEndDt.time,
    timezone: gig.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
    existingImageUrl: gig.image ?? null,
    tagIds: normalizeGigTagLabels((gig as Record<string, unknown>).tags)
      .map((name) => tagsByName.get(name.trim().toLowerCase())?.id)
      .filter((id): id is number => id !== undefined),
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() => requestExit(() => window.history.back())}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>

        <GigForm
          title="Edit Gig"
          submitLabel="Save Changes"
          onSubmit={handleSubmit}
          onDelete={() => setIsDeleteModalOpen(true)}
          onDirtyChange={setIsFormDirty}
          showDelete
          isLoading={isUpdating || isUploading}
          initialData={initialData}
          isEditMode
          originalStartDate={startDt.date}
        />
      </main>

      <BottomNav />

      {isDeleteModalOpen && (
        <DeleteGigModal
          gigTitle={gig.title}
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}

      {isConfirmOpen && <UnsavedChangesModal onConfirm={confirmExit} onCancel={cancelExit} />}
    </div>
  );
}
